// packages/frontend/src/features/tonight/Tonight.tsx
//
// Tonight page (Plan 12): the day-first home surface, mounted at `/`. Composes
// entirely from `useTonight()` (Dexie-only, no network call on mount) plus the
// cook-session awareness widgets that used to live on the Library. Page order
// (brief req. 2): wake-up nudge -> active cooks -> tonight's dinner -> today's
// prep tasks -> quick-links escape hatches into the rest of the app.
import type { JSX } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { CalendarDays, ShoppingCart, BookOpen, Plus } from "lucide-react";
import { sync } from "../../sync/SyncService.js";
import { setFocusedSessionId } from "../cook-sessions/focusedSession.js";
import { WakeUpBanner } from "../cook-sessions/WakeUpBanner.js";
import { ActiveCooksCard } from "../cook-sessions/ActiveCooksCard.js";
import { AddRecipeSheet } from "../imports/AddRecipeSheet.js";
import { TonightDinnerCard } from "./TonightDinnerCard.js";
import { DoTodayCard } from "./DoTodayCard.js";
import { useTonight } from "./useTonight.js";

const QUICK_LINK_CLASS =
  "flex min-h-12 flex-col items-center justify-center gap-1 rounded-lg border border-zinc-800 bg-zinc-900/40 px-2 py-2 text-center text-xs font-medium text-zinc-300 transition-colors hover:bg-zinc-800/60";

export function Tonight(): JSX.Element {
  const data = useTonight();
  const [starting, setStarting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();

  async function handleStartCooking(recipeId: string): Promise<void> {
    if (starting) return; // double-start guard
    setStarting(true);
    try {
      // Plan 14: Tonight already knows today's slot (unlike RecipeView, which
      // has to auto-detect via planLinkage.detectPlanRef) — the tapped dinner
      // card only ever starts cooking the recipe shown in today's dinner
      // slot, so link straight to it. Mirrors TonightDinnerCard's own
      // `primary = slot.items[0]` selection (the "first item" the card
      // renders/acts on), so this only matches the same slot the user saw.
      const dinnerSlot = data.todayDay?.slots.find(
        (s) => s.mealType === "dinner" && s.items[0]?.kind === "recipe" && s.items[0].recipeId === recipeId,
      );
      const planRef = dinnerSlot ? { weekStartDate: data.weekStart, slotId: dinnerSlot.id } : undefined;
      const session = await sync.createCookSessionOptimistic({
        recipeId,
        ...(planRef ? { planRef } : {}),
      });
      setFocusedSessionId(session.id);
      void navigate("/cook/" + session.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start cooking");
      setStarting(false); // re-enable on failure (success navigates away)
    }
  }

  const shoppingHref = data.activeShoppingListId ? `/shopping/${data.activeShoppingListId}` : "/shopping";

  return (
    <div className="flex flex-col gap-4">
      <WakeUpBanner />
      <ActiveCooksCard />
      <TonightDinnerCard
        data={data}
        onStartCooking={(recipeId) => { void handleStartCooking(recipeId); }}
      />
      <DoTodayCard data={data} />

      <div className="grid grid-cols-4 gap-2">
        <Link to="/plan" className={QUICK_LINK_CLASS}>
          <CalendarDays className="h-5 w-5" aria-hidden />
          This week
        </Link>
        <Link to={shoppingHref} className={QUICK_LINK_CLASS}>
          <ShoppingCart className="h-5 w-5" aria-hidden />
          Shopping
        </Link>
        <Link to="/recipes" className={QUICK_LINK_CLASS}>
          <BookOpen className="h-5 w-5" aria-hidden />
          Library
        </Link>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className={QUICK_LINK_CLASS}
        >
          <Plus className="h-5 w-5" aria-hidden />
          + New recipe
        </button>
      </div>

      <AddRecipeSheet open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
